import { apiReformat, loadZones } from "../modules/nysm/NwsAlerts";

const NAMESPACE = "currentNwsAlerts";
const actions = {
  LOAD: NAMESPACE + "/LOAD",
  LOAD_SUCCESS: NAMESPACE + "/LOAD_SUCCESS",
  LOAD_ZONES: NAMESPACE + "/LOAD_ZONES",
  LOAD_NO_UPDATE: NAMESPACE + "/LOAD_NO_UPDATE",
  LOAD_ERROR: NAMESPACE + "/LOAD_ERROR",
};
export { actions };

const initialState = {};
const initialAreaState = {
  loading: false,
  error: false,
  alerts: null,
  updated: null,
  loadTime: null,
};

const pollers = {};

const currentNwsAlerts = (state = initialState, action) => {
  if (Object.keys(actions).find((key) => actions[key] === action.type)) {
    const newState = { ...state };
    newState[action.area] = reduceArea(state[action.area], action);
    return newState;
  }
  return state;
};
export default currentNwsAlerts;

const reduceArea = (state = initialAreaState, action) => {
  switch (action.type) {
    case actions.LOAD: {
      const newState = {
        ...state,
        loading: true,
        error: false,
      };
      return newState;
    }
    case actions.LOAD_SUCCESS: {
      const newState = {
        ...state,
        loading: false,
        error: false,
        alerts: action.alerts,
        updated: action.updated,
        zonesLoaded: false,
        loadTime: action.loadTime,
      };
      return newState;
    }
    case actions.LOAD_ZONES: {
      const newState = {
        ...state,
        alerts: action.alerts,
        zonesLoaded: true
      }
      return newState;
    }
    case actions.LOAD_NO_UPDATE: {
      const newState = {
        ...state,
        loading: false,
        error: false,
        loadTime: action.loadTime,
      };
      return newState;
    }
    case actions.LOAD_ERROR: {
      const newState = {
        ...state,
        loading: false,
        error: true,
      };
      return newState;
    }
    default:
      return state;
  }
};

const interval = 30000; // 30 seconds
const in_progress = {};

export const loadAlerts = (area, forceUpdate = false) => async (dispatch, getState) => {
  if (in_progress[area]) {
    // Request in progress from someone else. No actions to dispatch.
    return in_progress[area];
  }
  const state = getState()[NAMESPACE];
  if (area in state && state[area].data &&
    ((new Date().valueOf() - state[area].loadTime) < interval && !forceUpdate)) {
      // Already have data. No actions to dispatch.
      return state[area].data;
  }
  dispatch({
    type: actions.LOAD,
    area,
  });

  const promise = fetch(
    `https://api.weather.gov/alerts/active/area/${area}`,
    { headers: { Accept: "application/geo+json" } }
  )
    .then(res => res.json())
    .then(latest => {
      const currentAlerts = getState()[NAMESPACE];
      const needUpdate =
        !currentAlerts.hasOwnProperty(area) ||
        !currentAlerts[area].alerts ||
        currentAlerts[area].updated !== latest.updated;

      if (needUpdate) {
        const alerts = apiReformat(latest);
        dispatch({
          type: actions.LOAD_SUCCESS,
          area,
          alerts,
          updated: latest.updated,
          loadTime: new Date().valueOf(),
        });
        loadZones(alerts).then(newAlerts => {
          if (alerts === getState()[NAMESPACE][area].alerts) {
            dispatch({
              type: actions.LOAD_ZONES,
              area,
              alerts: newAlerts,
            });
          }
        });
        delete in_progress[area];
        return alerts;
      } else {
        dispatch({
          type: actions.LOAD_NO_UPDATE,
          area,
          loadTime: new Date().valueOf(),
        });
        delete in_progress[area];
        return currentAlerts[area];
      }
    })
    .catch(err => {
      console.error("Failed loading NWS alerts for area", area);
      console.error(err);
      dispatch({
        type: actions.LOAD_ERROR,
        area
      });
      delete in_progress[area];
      return undefined;
    });
  in_progress[area] = promise;
  return promise;
};

// --- WATCH ---

export const watch = (area, watcher) => (dispatch, getState) => {
  if (!pollers.hasOwnProperty(area)) {
    pollers[area] = { watchers: [], polling: false, timerId: null };
  }
  const poller = pollers[area];
  if (poller.watchers.indexOf(watcher) < 0) {
    poller.watchers.push(watcher);
  } else {
    console.warn("Watch called, but watcher already registered.");
  }

  checkPoller(area, dispatch, getState);
};

export const unwatch = (area, watcher) => (dispatch, getState) => {
  if (!pollers.hasOwnProperty(area)) {
    console.warn("Unwatch called, but no watchers have been registered.");
    return;
  }
  const poller = pollers[area];
  const index = poller.watchers.indexOf(watcher);
  if (index < 0) {
    console.warn("Unwatch called, but watcher was not registered.");
  } else {
    poller.watchers.splice(index, 1);
  }

  checkPoller(area, dispatch, getState);
};

// If there are watchers but not currently polling, force a poll.
// If there are no watchers but it is currently polling, cancel the poll.
const checkPoller = (area, dispatch, getState) => {
  const poller = pollers[area];
  if (poller.watchers.length > 0 && !poller.polling) {
    schedulePoll(area, dispatch, getState, true);
    poller.polling = true;
  }
  if (poller.watchers.length === 0 && poller.polling) {
    stopPolling(area, dispatch, getState);
    poller.polling = false;
  }
};

// Schedules another poll, but only if there are watchers. Pass force = true to
// force a poll immediately.
const schedulePoll = (area, dispatch, getState, force = false) => {
  const poller = pollers[area];

  const poll = (force = false) => {
    poller.timerId = null;
    // Schedule the next poll when the area bit is finished.
    loadAlerts(area, force)(dispatch, getState).then(() =>
      schedulePoll(area, dispatch, getState)
    );
  };

  if (force) {
    poll(force);
  }

  if (poller.timerId) {
    clearTimeout(poller.timerId);
    poller.timerId = null;
  }

  if (poller.watchers.length > 0) {
    poller.timerId = setTimeout(poll, interval);
  }
};

const stopPolling = (area, dispatch, getState) => {
  const poller = pollers[area];
  if (poller.timerId) {
    clearTimeout(poller.timerId);
  }
  poller.timerId = null;
};

export const selectAlerts = state => state[NAMESPACE];